import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@paneon/auth';
import { PaneonThemeProvider } from '@paneon/theme';
import { StorageProvider } from '@paneon/storage';
import { LoadingSpinner } from '@paneon/ui';

// Initialize Firebase
import './config/firebase';
import { appConfig } from './config/app.config';

// Pages
import { Dashboard } from './pages/Dashboard';
import { LoginPage } from './pages/LoginPage';
import { PetsPage } from './pages/PetsPage';
import { FeedPage } from './pages/FeedPage';
import { SettingsPage } from './pages/SettingsPage';

// Layout
import { AppLayout } from './components/AppLayout';

/**
 * Protected route wrapper
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen text="Laden..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * App routes
 */
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="pets/*" element={<PetsPage />} />
        <Route path="feed/*" element={<FeedPage />} />
        <Route path="settings/*" element={<SettingsPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Main App component
 */
export function App() {
  return (
    <BrowserRouter>
      <PaneonThemeProvider defaultThemeId={appConfig.defaults.themeId}>
        <AuthProvider>
          <StorageProvider apiConfig={{ baseUrl: appConfig.apiBaseUrl }}>
            <AppRoutes />
          </StorageProvider>
        </AuthProvider>
      </PaneonThemeProvider>
    </BrowserRouter>
  );
}
